import { useEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { Icon } from "./Icon";

export interface FilterOption {
  value: string;
  label: string;
  /** Drawn before the label in the menu — the owner filter puts the avatar here. */
  leading?: ReactNode;
}

/** Options A→Z by label. Lists that come from data (accounts, owners, tags)
 *  read as a directory, not in insertion or priority order. */
export function alphabetical(options: FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * A filter chip that opens a menu of options. Selecting one calls onSelect with
 * the value (empty string clears). The chip shows "solid" when a value is set,
 * matching the draft's dashed→solid affordance. Closes on outside click and
 * Escape; the menu is keyboard reachable.
 */
export function FilterMenu({
  label,
  options,
  value,
  onSelect,
  lockedReason,
}: {
  label: string;
  options: FilterOption[];
  value: string | null;
  onSelect: (value: string) => void;
  /** Fixes the filter at `value`: the chip shows it, disabled, with this as its tooltip. */
  lockedReason?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const set = value !== null && value !== "";
  const current = options.find((o) => o.value === value);

  useCloseOnOutside(ref, open, () => setOpen(false));

  return (
    <div ref={ref} className="relative">
      <FilterChip label={label} set={set} open={open} popup="listbox" onToggle={() => setOpen((o) => !o)} onClear={() => onSelect("")} lockedReason={lockedReason}>
        {current?.label ?? value}
      </FilterChip>

      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-52 overflow-auto rounded-xl border border-[var(--border)] bg-[var(--bg)] p-1 shadow-lg"
        >
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => {
                  onSelect(o.value);
                  setOpen(false);
                }}
                className={[
                  "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-gray-100 focus-visible:focus-ring",
                  o.value === value ? "font-medium text-[var(--text)]" : "text-gray-700",
                ].join(" ")}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {o.leading}
                  <span className="truncate">{o.label}</span>
                </span>
                {o.value === value && <Icon name="check" className="text-brand-600" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Close a popover on a click outside `ref` or on Escape, while `open`. */
export function useCloseOnOutside(ref: RefObject<HTMLElement | null>, open: boolean, close: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, open, close]);
}

/**
 * The filter chip itself: dashed until set, solid with the value (children)
 * and a clear control once set. Shared by every filter popover so the chips
 * on a page cannot drift apart.
 */
export function FilterChip({
  label,
  set,
  open,
  popup,
  onToggle,
  onClear,
  lockedReason,
  children,
}: {
  label: string;
  set: boolean;
  open: boolean;
  popup: "listbox" | "dialog";
  onToggle: () => void;
  onClear: () => void;
  /** Disabled at its value: no menu, no clear control. The reason is its tooltip. */
  lockedReason?: string;
  children?: ReactNode;
}) {
  if (lockedReason) {
    return (
      <button
        type="button"
        disabled
        title={lockedReason}
        className="inline-flex h-7 cursor-not-allowed items-center gap-1.5 rounded-full border border-[var(--border)] bg-gray-100 px-3 text-sm font-medium text-[var(--text-2)]"
      >
        {label}
        <b className="font-medium">{children}</b>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-haspopup={popup}
      aria-expanded={open}
      className={[
        "inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors focus-visible:focus-ring",
        set
          ? "border border-[var(--border-strong)] bg-[var(--bg)] text-[var(--text)]"
          : "border border-dashed border-[var(--border-strong)] text-gray-700 hover:border-solid hover:bg-gray-100",
      ].join(" ")}
    >
      {label}
      {set && (
        <>
          <b className="font-medium">{children}</b>
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onClear();
              }
            }}
            className="text-[var(--text-3)] hover:text-[var(--text)]"
          >
            <Icon name="x" />
          </span>
        </>
      )}
      {!set && <Icon name="chevrons-ud" className="text-[var(--text-3)]" />}
    </button>
  );
}
